import moment from 'moment-timezone';
import { read_api_error } from '../../../utils/api_error_message';

// A-52 extension: an Ops-entered date/time that affects pay is warned about, never blocked. The server
// computes the warning (409 PAY_TIME_WARNING {warnings, facts, message}); a resend carries
// pay_time_ack {warnings, note} and the server records it for review.

const IST = 'Asia/Kolkata';
export const PAY_TIME_WARNING = 'PAY_TIME_WARNING';
// Mirrors pay_input_ack.note VARCHAR(500).
export const ACK_NOTE_MAX = 500;

// 'time': session/student dialogs (owner rule: time facts only, no money or pay words).
// 'effect': Teacher Payments screens, which may say what the change does to pay.
export const DETAIL_TIME = 'time';
export const DETAIL_EFFECT = 'effect';

// Per code: a time-only sentence, and (payment screens) what it affects.
export const WARNING_TEXT = {
  BEFORE_BOOKING: { time: 'The request time is before the class was booked.' },
  OVER_7_DAYS: { time: 'The request time is more than 7 days before the class.' },
  BEFORE_EARLIER_CHANGE: { time: 'The request time is before an earlier change to this class.' },
  START_IN_PAST: { time: 'The new start time is in the past.' },
  CROSSES_PAY_MONTH: {
    time: 'The new start is in a different month from the current start.',
    effect: 'The class moves to another pay month.'
  },
  PAY_MONTH_CLOSED: {
    time: 'This change touches a month that is already closed for this teacher.',
    effect: 'A closed pay month is touched; the payments team reviews it.'
  },
  AFTER_CLASS_END: { time: 'The class has already ended.' },
  LIVE_DATE_CROSSES_CUTOFF: {
    time: 'The new live date is on the other side of 8 Jun 2026 from the current one.',
    effect: 'This moves the teacher between Old and New pay for open months.'
  },
  LIVE_DATE_BEFORE_PROFILE: { time: 'The live date is before the teacher profile was created.' },
  LIVE_DATE_AFTER_FIRST_CLASS: {
    time: "The live date is after the teacher's first finished class."
  },
  RATE_BACKDATED: {
    time: 'Effective from is before today (IST).',
    effect: 'Classes from that date in open months are repriced at this rate.'
  },
  MONTH_NOT_ENDED: {
    time: 'This month has not ended yet (IST).',
    effect: 'Classes held after the close would not be in any closed month.'
  },
  PURCHASE_BACKDATED: { time: 'The purchase date is in the past.' }
};

// Known fact keys (server facts{}), in display order. Unknown keys are shown with a readable label.
const FACT_LABELS = [
  ['requested_at', 'Request time'],
  ['booked_at', 'Class booked'],
  ['earlier_change_at', 'Earlier change'],
  ['old_start', 'Current start'],
  ['new_start', 'New start'],
  ['class_end', 'Class ended'],
  ['old_duration', 'Current duration'],
  ['new_duration', 'New duration'],
  ['closed_month', 'Closed month'],
  ['old_live_date', 'Current live date'],
  ['new_live_date', 'New live date'],
  ['cutoff_date', 'Cut-off'],
  ['profile_created', 'Profile created'],
  ['first_class_at', 'First finished class'],
  ['effective_from', 'Effective from'],
  ['month', 'Month'],
  ['month_end', 'Month ends'],
  ['old_purchase_date', 'Current purchase date'],
  ['purchase_date', 'Purchase date'],
  ['next_billing_date', 'Next billing date'],
  ['today', 'Today']
];
const LABEL_OF = FACT_LABELS.reduce((acc, [k, l]) => ({ ...acc, [k]: l }), {});
const ORDER_OF = FACT_LABELS.reduce((acc, [k], i) => ({ ...acc, [k]: i }), {});

// Anything that names money or pay; dropped from 'time' dialogs even if the server sends it.
const MONEY_RE = /pay|charge|penalt|amount|rate|fee|₹|%|inr|rupee|categor|incentive|earn/i;

const humanize = (key) => {
  const s = String(key).replace(/_/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// One fact value in IST words: instants → "2 Nov 2026, 10:00 AM IST", dates → "2 Nov 2026",
// months → "Nov 2026", *duration → "30 min".
export function fact_value_text(key, value) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number')
    return /duration|minutes/.test(key) ? `${value} min` : String(value);
  const s = String(value);
  if (/^\d{4}-\d{2}$/.test(s)) return moment(`${s}-01`, 'YYYY-MM-DD', true).format('MMM YYYY');
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return moment(s, 'YYYY-MM-DD', true).format('D MMM YYYY');
  const m = moment.utc(s, [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss'], true);
  if (m.isValid()) return `${m.tz(IST).format('D MMM YYYY, h:mm A')} IST`;
  return s;
}

// [{key, label, text}] for display; 'time' mode drops anything that names money or pay.
export function fact_lines(facts, detail = DETAIL_TIME) {
  if (!facts || typeof facts !== 'object' || Array.isArray(facts)) return [];
  return Object.keys(facts)
    .filter((k) => typeof facts[k] !== 'object' || facts[k] === null)
    .map((k) => ({ key: k, label: LABEL_OF[k] || humanize(k), text: fact_value_text(k, facts[k]) }))
    .filter((f) => f.text)
    .filter((f) => detail !== DETAIL_TIME || (!MONEY_RE.test(f.key) && !MONEY_RE.test(f.text)))
    .sort((a, b) => (ORDER_OF[a.key] ?? 999) - (ORDER_OF[b.key] ?? 999));
}

// The 409 body as a warning, or null for any other error.
export function read_pay_time_warning(e) {
  const { body, code, message } = read_api_error(e);
  if (code !== PAY_TIME_WARNING) return null;
  const warnings = Array.isArray(body.warnings)
    ? body.warnings.filter((w) => typeof w === 'string' && w)
    : [];
  if (!warnings.length) return null;
  const facts = body.facts && typeof body.facts === 'object' ? body.facts : {};
  return { warnings, facts, message };
}

// What the step shows. 'time' mode never shows the server's sentence (it may name pay); it builds
// one line per code instead. 'effect' mode adds what each code does to pay.
export function pay_time_view(w, detail = DETAIL_TIME) {
  const lines = w.warnings.map((code) => {
    const t = WARNING_TEXT[code];
    if (!t) return detail === DETAIL_TIME ? 'This time needs a second look.' : humanize(code);
    return detail === DETAIL_EFFECT && t.effect ? `${t.time} ${t.effect}` : t.time;
  });
  return {
    title: 'Check this time before saving',
    lines: Array.from(new Set(lines)),
    message: detail === DETAIL_EFFECT ? w.message || '' : '',
    facts: fact_lines(w.facts, detail)
  };
}

// '' = valid: a 1-500 character note is required.
export function ack_note_error(note) {
  const text = String(note || '').trim();
  if (!text) return 'Write why this is right (required).';
  if (text.length > ACK_NOTE_MAX) return `The note must be at most ${ACK_NOTE_MAX} characters.`;
  return '';
}

export const build_pay_time_ack = (warnings, note) => ({
  warnings: [...warnings],
  note: String(note || '').trim()
});

export const with_pay_time_ack = (payload, ack) =>
  ack ? { ...payload, pay_time_ack: ack } : payload;
